import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/common/DashboardLayout';
import CommentSection from '../../components/common/CommentSection';
import { useAuth } from '../../context/AuthContext';
import * as hubAnnouncementService from '../../services/hubAnnouncement.service';
import * as announcementCommentService from '../../services/hubAnnouncementComment.service';
import { renderRichText } from '../../utils/richText';

export default function AnnouncementDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [announcement, setAnnouncement] = useState(null);
  const [more, setMore] = useState([]);
  const [error, setError] = useState(null);
  const [showNudge, setShowNudge] = useState(false);

  const getComments = useCallback(() => announcementCommentService.getComments(id), [id]);
  const createComment = useCallback((body, parentCommentId, imageUrl) => announcementCommentService.createComment(id, body, parentCommentId, imageUrl), [id]);

  useEffect(() => {
    hubAnnouncementService.getAnnouncementById(id)
      .then(setAnnouncement)
      .catch(err => setError(err.response?.data?.error || 'Could not load this announcement.'));
    hubAnnouncementService.getRecentAnnouncements()
      .then(list => setMore((list || []).filter(a => a.id !== id).slice(0, 3)))
      .catch(() => { /* non-critical */ });
  }, [id]);

  if (error) {
    return (
      <DashboardLayout>
        <div className="max-w-2xl mx-auto bg-red-50 border border-red-200 rounded-2xl px-4 py-3 text-sm text-red-700">
          {error}
          <button onClick={() => navigate('/dashboard')} className="block mt-2 underline">
            Back to Dashboard
          </button>
        </div>
      </DashboardLayout>
    );
  }

  if (!announcement) {
    return (
      <DashboardLayout>
        <div className="max-w-2xl mx-auto text-sm text-gray-400">Loading…</div>
      </DashboardLayout>
    );
  }

  const dateLabel = new Date(announcement.created_at).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' });

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto">
        <button onClick={() => navigate(-1)} className="text-xs text-gray-400 hover:text-reset-brown mb-4">
          ← Back
        </button>

        <article className="bg-white rounded-2xl shadow-sm overflow-hidden">
          {announcement.image_url && (
            <img src={announcement.image_url} alt={announcement.image_alt_text || ''} className="w-full aspect-square object-cover" />
          )}
          <div className="p-6">
            <p className="text-xs text-gray-400 mb-2">{dateLabel}</p>
            <h1 className="font-serif text-2xl font-bold text-reset-brown mb-4">
              {announcement.title || 'Announcement'}
            </h1>
            {announcement.message_text && (
              <div className="text-sm text-gray-700 leading-relaxed">
                {renderRichText(announcement.message_text)}
              </div>
            )}
            {announcement.hub_url && (
              <a
                href={announcement.hub_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block mt-4 text-sm font-medium text-reset-terracotta hover:underline"
              >
                Quick link to the Hub →
              </a>
            )}
          </div>
        </article>

        {announcement.daily_nudge && (
          <div className="mt-4 bg-white rounded-2xl shadow-sm overflow-hidden">
            <button
              onClick={() => setShowNudge(v => !v)}
              className="w-full flex items-center justify-between px-5 py-3 text-sm font-medium text-reset-brown hover:bg-reset-cream/60 transition-colors"
            >
              <span>📧 Today's email nudge{announcement.daily_nudge.title ? `: ${announcement.daily_nudge.title}` : ''}</span>
              <span className="text-gray-400">{showNudge ? 'Hide' : 'View'}</span>
            </button>
            {showNudge && (
              <div className="border-t border-gray-50">
                {announcement.daily_nudge.html ? (
                  <iframe
                    title="Email nudge preview"
                    srcDoc={announcement.daily_nudge.html}
                    // A fully empty sandbox (no tokens at all) left the frame
                    // with no origin identity whatsoever — confirmed
                    // 2026-09-01 to silently fail to load the header banner
                    // image because of it (an identical iframe with just
                    // allow-same-origin added loaded the same image fine).
                    // Scripts, forms, popups, and top-navigation stay
                    // blocked either way since none of those tokens are
                    // present — this content is staff-authored, not
                    // arbitrary user input, so the origin alone is a safe
                    // trade for images actually rendering.
                    sandbox="allow-same-origin"
                    className="w-full"
                    style={{ minHeight: '480px', border: 'none' }}
                  />
                ) : (
                  <div className="px-5 pb-5 pt-1 text-sm text-gray-700 leading-relaxed">
                    {renderRichText(announcement.daily_nudge.body)}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {more.length > 0 && (
          <div className="mt-4 bg-white rounded-2xl shadow-sm p-5">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">More from the Hub</p>
            <div className="space-y-2">
              {more.map(a => (
                <button
                  key={a.id}
                  onClick={() => navigate(`/dashboard/announcements/${a.id}`)}
                  className="w-full text-left text-sm text-reset-brown hover:text-reset-terracotta transition-colors truncate"
                >
                  {a.title || a.message_text} →
                </button>
              ))}
            </div>
          </div>
        )}

        <p className="text-xs text-gray-400 mt-4">
          Looking for more? See all announcements on{' '}
          <button onClick={() => navigate('/dashboard/discussion')} className="text-reset-terracotta hover:underline">
            My Hive
          </button>.
        </p>

        <CommentSection
          getComments={getComments}
          createComment={createComment}
          deleteOwnComment={announcementCommentService.deleteOwnComment}
          currentUserId={user?.id}
        />
      </div>
    </DashboardLayout>
  );
}
